import { Injectable } from '@angular/core';
import { Auth } from '@aws-amplify/auth';
import { environment } from '../../../environments/environment';

const AUTH_TOKEN_KEY = 'auth_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private initialized = false;

  /**
   * Initialize Amplify Auth with Cognito configuration.
   * Should be called once at app startup.
   */
  initialize(): void {
    if (this.initialized) return;
    Auth.configure(environment.cognito);
    this.initialized = true;
  }

  /**
   * The Cognito ID token sent to the BFF, which exchanges it for the SRD
   * authn-service token server-side. Read synchronously by the HTTP
   * interceptor and the WebSocket effects.
   */
  getToken(): string | null {
    return sessionStorage.getItem(AUTH_TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    return !this.isTokenExpired(token);
  }

  /**
   * Check if the user has an active Cognito session and cache the current
   * Cognito ID token for outbound requests. If not authenticated, redirect to
   * Cognito SSO login. Returns true if the user ends up authenticated.
   */
  async checkAndAuthenticate(): Promise<boolean> {
    this.initialize();

    try {
      const idToken = await this.fetchCognitoIdToken();
      sessionStorage.setItem(AUTH_TOKEN_KEY, idToken);
      return true;
    } catch {
      // Not authenticated — redirect to Cognito SSO
      this.redirectToCognito();
      return false;
    }
  }

  /**
   * Refresh the cached Cognito ID token from the current Cognito session.
   * Amplify transparently uses the refresh token when the ID token is expired.
   * Returns the new token, or null if the Cognito session can't be refreshed.
   */
  async refreshToken(): Promise<string | null> {
    this.initialize();

    try {
      const idToken = await this.fetchCognitoIdToken();
      sessionStorage.setItem(AUTH_TOKEN_KEY, idToken);
      return idToken;
    } catch {
      // Cognito session expired — need to re-login
      this.redirectToCognito();
      return null;
    }
  }

  /**
   * Redirect to Cognito federated SSO login.
   */
  redirectToCognito(): void {
    Auth.federatedSignIn({ customProvider: environment.cognito.customProviderName });
  }

  /**
   * Sign out from both the local session and Cognito.
   */
  async signOut(): Promise<void> {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    try {
      await Auth.signOut();
    } catch {
      // Best-effort sign out
    }
  }

  /** Fetch a current (auto-refreshed) Cognito ID token from Amplify. */
  private async fetchCognitoIdToken(): Promise<string> {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodePayload(token);
      if (!payload || typeof payload.exp !== 'number') {
        return true;
      }
      const nowInSeconds = Math.floor(Date.now() / 1000);
      return payload.exp < nowInSeconds;
    } catch {
      return true;
    }
  }

  private decodePayload(token: string): { exp?: number } | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }
}
